<template>
  <InternalLayout>
    <header class="patient-care-header">
      <StepIndicator :steps="2" :currentStep="currentStep" @stepClick="onStepClick" />
      <SmallHeader tag="h1">Find a hospital</SmallHeader>
    </header>
    <HeaderText>{{ headline }}</HeaderText>
    <form v-if="currentStep === 2" class="needs-form form-step" @submit.prevent="onFinalSubmit">
      <CheckboxGrid :options="careNeeds" :selected="resources" @change="onCheck" />
      <BaseButton
        :variant="resources.length ? 'primary' : 'default'"
        class="submit-btn"
        :disabled="!location"
      >{{ resources.length ? 'Next' : 'Skip' }}</BaseButton>
    </form>
    <form v-else @submit.prevent="onLocationSubmit" class="location-form form-step">
      <LocationInput @change="onLocationInput" />
      <BaseButton variant="primary" class="submit-btn" :disabled="!location">Next</BaseButton>
    </form>
  </InternalLayout>
</template>

<script>
import BaseButton from '@/components/base-button';
import CheckboxGrid from '@/components/checkbox-grid';
import HeaderText from '@/components/header-text';
import InternalLayout from '@/components/internal-layout';
import LocationInput from '@/components/location-input';
import SmallHeader from '@/components/small-header';
import StepIndicator from '@/components/step-indicator';
import Vue from 'vue';
import get from 'lodash/get';
import { CareNeeds } from '@/store/types';
import { RouterAliases } from '@/router';

export default {
  components: {
    BaseButton,
    CheckboxGrid,
    HeaderText,
    InternalLayout,
    LocationInput,
    SmallHeader,
    StepIndicator,
  },
  props: {
    currentStep: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      address: null,
      location: null,
      resources: [],
    };
  },
  computed: {
    headline() {
      return this.currentStep === 2
        ? 'Your patient needs:'
        : 'Enter your patient’s address';
    },
    careNeeds() {
      return Object.entries(CareNeeds).map(([k, v]) => ({
        value: k,
        label: v,
      }));
    },
  },
  watch: {
    currentStep: {
      handler(newVal) {
        if (newVal !== 1 && !this.location) {
          this.$router.replace({
            name: RouterAliases.DOCTOR_ASSESSMENT,
            params: { step: 1 },
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    onCheck({ isChecked, value }) {
      if (!isChecked) {
        this.resources = this.resources.filter(item => item !== value);
      }
      if (isChecked && !this.resources.includes(value)) {
        this.resources = [...this.resources, value];
      }
    },
    onFinalSubmit() {
      this.$router.push({
        name: RouterAliases.DOCTOR_ASSESSMENT_RESULTS,
        query: {
          address: this.address,
          lat: this.location.lat,
          lng: this.location.lng,
          resources: this.resources.join(','),
        },
      });
    },
    onLocationInput(result) {
      this.location = result.center?.length
        ? {
            lng: result.center[0],
            lat: result.center[1],
          }
        : null;
      this.address = result.place_name;
    },
    onLocationSubmit() {
      this.$router.replace({
        name: RouterAliases.DOCTOR_ASSESSMENT,
        params: { step: 2 },
      });
    },
    onStepClick(step) {
      if (step === 2 && !this.location) return;
      this.$router.replace({
        name: RouterAliases.DOCTOR_ASSESSMENT,
        params: { step },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.patient-care-header {
  text-align: center;
}

.header-text {
  margin: 24px 0;
}

.step-indicator {
  margin: 8px auto 6px;
}

.form-step {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;

  @media screen and (min-width: 768px) {
    max-width: 100%;
    width: 542px;
  }
}

.location-form /deep/ {
  .search-results {
    background: var(--white);
    border-radius: 10px;
    color: var(--black);
    margin-top: 12px;
    padding: 0;
  }

  .address-list-item {
    align-items: flex-start;
    border-color: var(--off-white);
    display: flex;
    padding: 12px 32px 12px 16px;
  }

  .arrow {
    right: 16px;
  }

  .prediction:first-of-type .address-list-item {
    border-top: 0;
  }
}

.submit-btn {
  margin-top: auto;
}
</style>
